<script setup lang="ts">

import type { EvmConnectorId } from '~/service/EVM/config/types';
import { AsyncState } from '~/types/enums';

const props = defineProps<{
    isLoading?: boolean;
    isSuccess?: boolean;
    isError?: boolean;
    name: string;
    id: EvmConnectorId;
}>();

const emit = defineEmits<{
    (e: 'onConnect'): void;
}>();
const loadingState = ref<AsyncState>(AsyncState.IDLE);

const computedLoading = computed(() => props.isLoading && loadingState.value === AsyncState.IS_LOADING);
const { connector: { config } } = useProviderConfig()(['connector']);

const handleConnect = () => {
    loadingState.value = AsyncState.IS_LOADING;
    emit('onConnect');
};

watch([() => props.isSuccess, () => props.isError], (isNewSuccess, isNewError) => {
    if (isNewSuccess) loadingState.value = AsyncState.IS_SUCCESS;
    if (isNewError) loadingState.value = AsyncState.IS_ERROR;

    setTimeout(
        () => {
            loadingState.value = AsyncState.IDLE;
        },
        1000
    );
});

</script>

<template>
  <div :style="{ pointerEvents: computedLoading ? 'none' : 'auto' }"
       type="button"
       role="button"
       v-bind="$attrs"
       :aria-label="`connect to ${name}`"
       class="connectors-row"
       @click="handleConnect()"
  >
    <div class="grow display-flex align-items-center gap-12">
      <IconTypes :size="36"
                 :name="config[id]?.icon || ''"
      />
      <p class="display-500-accent">
        {{ name }}
      </p>
    </div>
    <LoaderDots v-if="computedLoading" />
  </div>
</template>

<style lang="scss">

%spacing-x {
  padding-right: $spacing-600;
  padding-left: $spacing-600;

  @include tablet-768 {
    padding-right: $spacing-400;
    padding-left: $spacing-400;
  }
}

.connectors {
  &-row {
    display: flex;
    align-items: center;
    align-self: stretch;
    padding-top: 10px;
    padding-bottom: 10px;
    transition: background-color 0.3s ease;
    cursor: pointer;
    gap: 12px;

    @extend %spacing-x;

    &:hover {
      background-color: var(--bg-active-elements);
    }
  }
}
</style>
